/**
 * SunSwap API Service - TRON DEX
 *
 * SunSwap (sun.io) is the main DEX on TRON blockchain.
 * Used for TRON ↔ TRON swaps.
 *
 * Uses TronGrid to call SunSwap V2 Router contract directly.
 * All endpoints, router addresses, and tokens switch via networkConfig.
 */

import { getSunswapConfig } from './networkConfig';

function getTrongridApi(): string {
  return getSunswapConfig().trongridApi;
}

function getRouterAddress(): string {
  return getSunswapConfig().routerAddress;
}

// Dynamic token lookup reflecting current network
export const TRON_TOKENS: Record<string, string> = new Proxy({} as Record<string, string>, {
  get(_target, prop: string) {
    return getSunswapConfig().tokens[prop];
  },
  ownKeys() {
    return Object.keys(getSunswapConfig().tokens);
  },
  getOwnPropertyDescriptor(_target, prop: string) {
    const tokens = getSunswapConfig().tokens;
    if (prop in tokens) {
      return { configurable: true, enumerable: true, value: tokens[prop] };
    }
    return undefined;
  },
  has(_target, prop: string) {
    return prop in getSunswapConfig().tokens;
  },
});

// getHexAddresses is used indirectly through base58ToHex

/**
 * Convert base58 TRON address to hex (without 41 prefix)
 */
function base58ToHex(address: string): string {
  const config = getSunswapConfig();
  const hexAddrs = config.hexAddresses as Record<string, string>;
  // Check if we have a known mapping
  for (const [symbol, base58] of Object.entries(config.tokens)) {
    if (base58 === address && hexAddrs[symbol]) {
      return hexAddrs[symbol];
    }
  }
  // For unknown addresses, we'd need proper base58 decoding
  // For now, throw an error
  throw new Error(`Unknown TRON address: ${address}`);
}

export interface SunswapQuoteRequest {
  tokenIn: string;
  tokenOut: string;
  amountIn: string; // In smallest units (SUN for TRX)
  slippage?: number; // Percentage, default 0.5
}

export interface SunswapRoute {
  tokenIn: string;
  tokenOut: string;
  amountIn: string;
  amountOut: string;
  priceImpact: string;
  route: Array<{
    pool: string;
    tokenIn: string;
    tokenOut: string;
    amountIn: string;
    amountOut: string;
  }>;
  fee: string;
}

export interface SunswapSwapParams {
  userAddress: string;
  tokenIn: string;
  tokenOut: string;
  amountIn: string;
  minAmountOut: string;
  deadline?: number; // Unix timestamp
}

export interface SunswapTransaction {
  to: string;
  data: string;
  value: string;
}

/**
 * Get TRON token address from symbol (reads from current network config)
 */
export function getTronTokenAddress(symbol: string, contractAddress?: string): string | null {
  if (contractAddress) {
    return contractAddress;
  }
  return getSunswapConfig().tokens[symbol] || null;
}

class SunswapApiService {
  /**
   * Get swap quote from SunSwap via TronGrid contract call
   */
  async getQuote(request: SunswapQuoteRequest): Promise<SunswapRoute> {
    // Get hex addresses for the tokens
    const tokenInHex = base58ToHex(request.tokenIn);
    const tokenOutHex = base58ToHex(request.tokenOut);

    // Encode getAmountsOut(uint256 amountIn, address[] path) parameters
    const amountInHex = BigInt(request.amountIn).toString(16).padStart(64, '0');
    const arrayOffset = '0000000000000000000000000000000000000000000000000000000000000040';
    const arrayLength = '0000000000000000000000000000000000000000000000000000000000000002';
    const addr1 = tokenInHex.padStart(64, '0');
    const addr2 = tokenOutHex.padStart(64, '0');

    const parameter = amountInHex + arrayOffset + arrayLength + addr1 + addr2;

    const response = await fetch(`${getTrongridApi()}/wallet/triggerconstantcontract`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        owner_address: request.tokenIn, // Any valid address works for view calls
        contract_address: getRouterAddress(),
        function_selector: 'getAmountsOut(uint256,address[])',
        parameter,
        visible: true,
      }),
    });

    if (!response.ok) {
      throw new Error(`TronGrid API error: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.constant_result || !data.constant_result[0]) {
      const errorMsg = data.result?.message || 'Failed to get quote from SunSwap';
      throw new Error(errorMsg);
    }

    // Decode result - returns array of uint256 [amountIn, amountOut]
    const resultHex = data.constant_result[0];
    // Skip first 64 chars (offset) + 64 chars (length) + 64 chars (amountIn) = 192, then read amountOut
    const amountOutHex = resultHex.slice(192, 256);
    const amountOut = BigInt('0x' + amountOutHex).toString();

    return {
      tokenIn: request.tokenIn,
      tokenOut: request.tokenOut,
      amountIn: request.amountIn,
      amountOut,
      priceImpact: '0', // Would need pool reserves to calculate
      route: [{ pool: getRouterAddress(), tokenIn: request.tokenIn, tokenOut: request.tokenOut, amountIn: request.amountIn, amountOut }],
      fee: '0.3', // SunSwap standard fee is 0.3%
    };
  }

  /**
   * Get quote with simplified parameters
   */
  async getSimpleQuote(
    fromSymbol: string,
    toSymbol: string,
    amount: string,
    _slippagePercent: number = 0.5,
    fromContract?: string,
    toContract?: string
  ): Promise<SunswapRoute> {
    const tokenIn = getTronTokenAddress(fromSymbol, fromContract);
    const tokenOut = getTronTokenAddress(toSymbol, toContract);

    if (!tokenIn) {
      throw new Error(`Unknown TRON token: ${fromSymbol}`);
    }
    if (!tokenOut) {
      throw new Error(`Unknown TRON token: ${toSymbol}`);
    }

    return this.getQuote({
      tokenIn,
      tokenOut,
      amountIn: amount,
    });
  }

  /**
   * Build swap transaction
   * Note: TRON uses trigger smart contract calls
   */
  buildSwapTransaction(params: SunswapSwapParams): SunswapTransaction {
    // SunSwap V2 Router address (dynamic per network)
    const ROUTER_ADDRESS = getRouterAddress();

    // Calculate deadline (default: 20 minutes from now)
    const deadline = params.deadline || Math.floor(Date.now() / 1000) + 20 * 60;

    // Build the function selector and parameters for swapExactTokensForTokens
    // This is simplified - actual implementation needs proper ABI encoding
    const data = {
      function: 'swapExactTokensForTokens',
      params: {
        amountIn: params.amountIn,
        amountOutMin: params.minAmountOut,
        path: [params.tokenIn, params.tokenOut],
        to: params.userAddress,
        deadline: deadline.toString(),
      },
    };

    return {
      to: ROUTER_ADDRESS,
      data: JSON.stringify(data),
      value: params.tokenIn === getSunswapConfig().tokens['TRX'] ? params.amountIn : '0',
    };
  }
}

export const sunswapApi = new SunswapApiService();
export { SunswapApiService };
